import { Bot, FolderKanban, ImageIcon, Laptop2, LayoutDashboard, Search, Settings, Shield, Trash2, Upload } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandShortcut } from "@/components/ui/command";
import { useTheme } from "@/hooks/useTheme";
import { useAgentStore } from "@/store/agentStore";
import { useUiStore } from "@/store/uiStore";

interface CommandPaletteProps {
  variant: "user" | "admin";
}

export function CommandPalette({ variant }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const open = useUiStore((state) => state.commandPaletteOpen);
  const setOpen = useUiStore((state) => state.setCommandPaletteOpen);
  const openAgentPanel = useAgentStore((state) => state.openPanel);

  const navItems = variant === "admin"
    ? [
        { label: "Admin Dashboard", icon: LayoutDashboard, action: () => navigate("/admin") },
        { label: "Tenants", icon: FolderKanban, action: () => navigate("/admin/tenants") },
        { label: "Audit Log", icon: Shield, action: () => navigate("/admin/audit") },
        { label: "System", icon: Settings, action: () => navigate("/admin/system") },
      ]
    : [
        { label: "My Drive", icon: FolderKanban, action: () => navigate("/app/files") },
        { label: "Dashboard", icon: LayoutDashboard, action: () => navigate("/app/home") },
        { label: "Devices", icon: Laptop2, action: () => navigate("/app/devices") },
        { label: "Photos", icon: ImageIcon, action: () => navigate("/app/photos") },
        { label: "Trash", icon: Trash2, action: () => navigate("/app/trash") },
      ];

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Search commands, pages, and actions..." />
      <CommandList>
        <CommandEmpty>No matching command found.</CommandEmpty>
        <CommandGroup heading="Navigate">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <CommandItem
                key={item.label}
                onSelect={() => {
                  item.action();
                  setOpen(false);
                }}
              >
                <Icon className="h-4 w-4 text-text-secondary" />
                <span>{item.label}</span>
              </CommandItem>
            );
          })}
        </CommandGroup>
        <CommandGroup heading="Quick actions">
          {variant === "user" ? (
            <>
              <CommandItem
                onSelect={() => {
                  navigate("/app/files");
                  setOpen(false);
                }}
              >
                <Upload className="h-4 w-4 text-text-secondary" />
                <span>Go to uploads</span>
                <CommandShortcut>U</CommandShortcut>
              </CommandItem>
              <CommandItem
                onSelect={() => {
                  openAgentPanel();
                  setOpen(false);
                }}
              >
                <Bot className="h-4 w-4 text-text-secondary" />
                <span>Open AI assistant</span>
                <CommandShortcut>⌘J</CommandShortcut>
              </CommandItem>
            </>
          ) : null}
          <CommandItem
            onSelect={() => {
              setTheme(theme === "dark" ? "light" : "dark");
              setOpen(false);
            }}
          >
            <Search className="h-4 w-4 text-text-secondary" />
            <span>Toggle theme</span>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
